import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/apiClient";

/**
 * LeadCaptureSection — Newsletter de novedades de JustSapiens Procure.
 * Envía POST /leads con fuente="landing".
 */
export function LeadCaptureSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Ingresa un correo electrónico válido.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    try {
      await api.post("/leads", { email: trimmed, fuente: "landing" });
      toast.success("Gracias. Te mantendremos al tanto de novedades de JustSapiens Procure.");
      setStatus("success");
      setEmail("");
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) ||
        "No pudimos guardar tu correo. Intenta más tarde.";
      setError(msg);
      toast.error(msg);
      setStatus("error");
    }
  };

  return (
    <section data-testid="lead-capture-section" className="border-t border-gray-200 bg-white">
      <div className="container-prose py-20 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6">
          <p className="text-xs uppercase tracking-[0.22em] text-brand-coral font-semibold mb-3">
            Recibe novedades
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl text-gray-900 mb-3">
            Mantente al día sobre actualizaciones de JustSapiens Procure.
          </h2>
          <p className="text-gray-600 max-w-lg">
            Te avisaremos sobre nuevas integraciones (SECOP, Bre-B), ampliación de cobertura
            normativa y mejoras al producto. Sin spam.
          </p>
        </div>
        <div className="md:col-span-6">
          {status === "success" ? (
            <div
              data-testid="lead-success-message"
              className="bg-brand-cream-soft border border-brand-burgundy/20 p-6 text-sm text-gray-800 rounded-sm"
            >
              <p className="font-medium text-brand-burgundy mb-1 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                Gracias.
              </p>
              <p>
                Te mantendremos al tanto. Mientras tanto, ya puedes{" "}
                <Link to="/register" className="text-brand-burgundy underline hover:text-brand-burgundy-light">
                  crear tu cuenta gratis
                </Link>{" "}
                y prevalidar tu primer caso.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="lead-capture-form"
              className="bg-brand-cream-soft border border-gray-200 p-6 rounded-sm"
            >
              <label htmlFor="lead-email" className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-semibold">
                Correo electrónico
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  id="lead-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="lead-email-input"
                  className="flex-1 border border-gray-300 px-3 h-11 text-sm focus:outline-none focus:border-brand-burgundy focus:ring-2 focus:ring-brand-burgundy/20 rounded-sm bg-white"
                  placeholder="tu.correo@entidad.gov.co"
                />
                <Button
                  type="submit"
                  disabled={status === "submitting"}
                  className="rounded-sm h-11 px-6 bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
                  data-testid="lead-submit-button"
                >
                  {status === "submitting" ? "Enviando…" : "Notificarme"}
                </Button>
              </div>
              {error && (
                <p data-testid="lead-error-message" className="mt-3 text-sm text-destructive">{error}</p>
              )}
              <p className="mt-3 text-xs text-gray-500">
                Tratamiento de datos conforme a la Ley 1581 de 2012. Sin spam.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
